import DxfParser from 'dxf-parser';

/* ──────────────────────────── Types ──────────────────────────── */

export interface Point2D { x: number; y: number }

export interface BBox {
  minX: number; minY: number;
  maxX: number; maxY: number;
  width: number; height: number;
}

export interface DxfPart {
  id: string;
  name: string;
  rawEntities: any[];
  blocks: Record<string, any>;
  bbox: BBox;
  entityCounts: Record<string, number>;
}

export interface Placement {
  partId: string;
  copyIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotated: boolean;
}

export interface NestSheet {
  placements: Placement[];
  utilization: number;
}

export interface NestingResult {
  sheets: NestSheet[];
  totalParts: number;
  placedParts: number;
  totalSheets: number;
  overallUtilization: number;
}

/* ──────────────────────────── DXF Parsing ──────────────────────────── */

export function parseDxfContent(content: string): { entities: any[]; blocks: Record<string, any> } {
  const parser = new (DxfParser as any)();
  const dxf = parser.parseSync(content);
  if (!dxf) throw new Error('Failed to parse DXF file');
  return { entities: dxf.entities || [], blocks: dxf.blocks || {} };
}

/* ──────────────────────────── Geometry helpers ──────────────────────────── */

function rotatePoint(px: number, py: number, cx: number, cy: number, angleRad: number): Point2D {
  const cos = Math.cos(angleRad), sin = Math.sin(angleRad);
  const dx = px - cx, dy = py - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/* ──────────────────────────── Bounding Box ──────────────────────────── */

function pointsFromEntity(entity: any, blocks?: Record<string, any>): Point2D[] {
  const pts: Point2D[] = [];
  switch (entity.type) {
    case 'LINE':
      if (entity.vertices) entity.vertices.forEach((v: any) => pts.push({ x: v.x, y: v.y }));
      break;
    case 'LWPOLYLINE':
    case 'POLYLINE':
      if (entity.vertices) entity.vertices.forEach((v: any) => pts.push({ x: v.x, y: v.y }));
      break;
    case 'CIRCLE':
      if (entity.center) {
        pts.push({ x: entity.center.x - entity.radius, y: entity.center.y - entity.radius });
        pts.push({ x: entity.center.x + entity.radius, y: entity.center.y + entity.radius });
      }
      break;
    case 'ARC':
      if (entity.center) {
        pts.push({ x: entity.center.x - entity.radius, y: entity.center.y - entity.radius });
        pts.push({ x: entity.center.x + entity.radius, y: entity.center.y + entity.radius });
      }
      break;
    case 'ELLIPSE':
      if (entity.center && entity.majorAxisEndPoint) {
        const mx = Math.abs(entity.majorAxisEndPoint.x);
        const my = Math.abs(entity.majorAxisEndPoint.y);
        const major = Math.sqrt(mx * mx + my * my);
        pts.push({ x: entity.center.x - major, y: entity.center.y - major });
        pts.push({ x: entity.center.x + major, y: entity.center.y + major });
      }
      break;
    case 'SPLINE':
      if (entity.controlPoints) entity.controlPoints.forEach((cp: any) => pts.push({ x: cp.x, y: cp.y }));
      if (entity.fitPoints) entity.fitPoints.forEach((fp: any) => pts.push({ x: fp.x, y: fp.y }));
      break;
    case 'SOLID':
    case '3DFACE':
      if (entity.points) entity.points.forEach((p: any) => { if (p) pts.push({ x: p.x, y: p.y }); });
      if (entity.vertices) entity.vertices.forEach((v: any) => pts.push({ x: v.x, y: v.y }));
      break;
    case 'INSERT':
      if (blocks && entity.name && blocks[entity.name]) {
        const block = blocks[entity.name];
        const sx = entity.xScale || 1, sy = entity.yScale || 1;
        const dx = entity.position?.x || 0, dy = entity.position?.y || 0;
        const rot = (entity.rotation || 0) * Math.PI / 180;
        for (const be of (block.entities || [])) {
          const blockPts = pointsFromEntity(be, blocks);
          for (const bp of blockPts) {
            let px = bp.x * sx, py = bp.y * sy;
            if (rot) {
              const rp = rotatePoint(px, py, 0, 0, rot);
              px = rp.x; py = rp.y;
            }
            pts.push({ x: dx + px, y: dy + py });
          }
        }
      }
      break;
    case 'POINT':
      if (entity.position) pts.push({ x: entity.position.x, y: entity.position.y });
      break;
    case 'DIMENSION':
      if (entity.anchorPoint) pts.push({ x: entity.anchorPoint.x, y: entity.anchorPoint.y });
      if (entity.middleOfText) pts.push({ x: entity.middleOfText.x, y: entity.middleOfText.y });
      break;
  }
  return pts;
}

export function computeBoundingBox(entities: any[], blocks?: Record<string, any>): BBox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const e of entities) {
    for (const p of pointsFromEntity(e, blocks)) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (!isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/* ──────────────────────────── SVG Path Generation ──────────────────────────── */

function arcFromBulge(x1: number, y1: number, x2: number, y2: number, bulge: number, yFlipped: boolean): string {
  const dx = x2 - x1, dy = y2 - y1;
  const chord = Math.sqrt(dx * dx + dy * dy);
  if (chord < 1e-8) return ` L ${x2} ${y2}`;
  const sagitta = Math.abs(bulge) * chord / 2;
  const radius = ((chord / 2) ** 2 + sagitta ** 2) / (2 * sagitta);
  const largeArc = Math.abs(bulge) > 1 ? 1 : 0;
  const sweep = (bulge > 0) === yFlipped ? 1 : 0;
  return ` A ${radius} ${radius} 0 ${largeArc} ${sweep} ${x2} ${y2}`;
}

export function transformInsertEntity(blockEntity: any, insert: any): any {
  const sx = insert.xScale || 1, sy = insert.yScale || 1;
  const dx = insert.position?.x || 0, dy = insert.position?.y || 0;
  const rot = (insert.rotation || 0) * Math.PI / 180;
  const e = JSON.parse(JSON.stringify(blockEntity));

  const tp = (p: any) => {
    if (!p) return;
    let px = p.x * sx, py = p.y * sy;
    if (rot) {
      const rp = rotatePoint(px, py, 0, 0, rot);
      px = rp.x; py = rp.y;
    }
    p.x = px + dx;
    p.y = py + dy;
  };

  if (e.vertices) e.vertices.forEach(tp);
  if (e.center) tp(e.center);
  if (e.position) tp(e.position);
  if (e.controlPoints) e.controlPoints.forEach(tp);
  if (e.fitPoints) e.fitPoints.forEach(tp);
  if (e.points) e.points.forEach(tp);
  return e;
}

/**
 * Convert a single DXF entity to SVG path `d` strings.
 * offsetX/offsetY = bbox.minX/minY (shifts to local origin).
 * scaleY = bbox.height (used to flip Y for SVG coordinate space).
 */
export function entityToSvgPaths(
  entity: any, offsetX: number, offsetY: number, scaleY: number,
  blocks?: Record<string, any>,
): string[] {
  const tx = (x: number) => x - offsetX;
  const ty = (y: number) => scaleY - (y - offsetY);
  const paths: string[] = [];

  switch (entity.type) {
    case 'LINE':
      if (entity.vertices?.length >= 2) {
        const [a, b] = entity.vertices;
        paths.push(`M ${tx(a.x)} ${ty(a.y)} L ${tx(b.x)} ${ty(b.y)}`);
      }
      break;

    case 'LWPOLYLINE':
    case 'POLYLINE': {
      const v = entity.vertices;
      if (v?.length >= 2) {
        let d = `M ${tx(v[0].x)} ${ty(v[0].y)}`;
        for (let i = 1; i < v.length; i++) {
          const prev = v[i - 1], curr = v[i];
          if (prev.bulge && prev.bulge !== 0) {
            d += arcFromBulge(tx(prev.x), ty(prev.y), tx(curr.x), ty(curr.y), prev.bulge, true);
          } else {
            d += ` L ${tx(curr.x)} ${ty(curr.y)}`;
          }
        }
        if (entity.shape) {
          const last = v[v.length - 1], first = v[0];
          if (last.bulge && last.bulge !== 0) {
            d += arcFromBulge(tx(last.x), ty(last.y), tx(first.x), ty(first.y), last.bulge, true);
          }
          d += ' Z';
        }
        paths.push(d);
      }
      break;
    }

    case 'CIRCLE':
      if (entity.center) {
        const cx = tx(entity.center.x), cy = ty(entity.center.y), r = entity.radius;
        paths.push(`M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy}`);
      }
      break;

    case 'ARC':
      if (entity.center) {
        const cx = tx(entity.center.x), cy = ty(entity.center.y), r = entity.radius;
        // dxf-parser already converts angles to radians
        const sa = entity.startAngle || 0;
        const ea = entity.endAngle != null ? entity.endAngle : 2 * Math.PI;
        const x1 = cx + r * Math.cos(sa), y1 = cy - r * Math.sin(sa);
        const x2 = cx + r * Math.cos(ea), y2 = cy - r * Math.sin(ea);
        let diff = ea - sa; if (diff < 0) diff += 2 * Math.PI;
        paths.push(`M ${x1} ${y1} A ${r} ${r} 0 ${diff > Math.PI ? 1 : 0} 0 ${x2} ${y2}`);
      }
      break;

    case 'ELLIPSE':
      if (entity.center && entity.majorAxisEndPoint) {
        const cx = tx(entity.center.x), cy = ty(entity.center.y);
        const mx = entity.majorAxisEndPoint.x, my = entity.majorAxisEndPoint.y;
        const rx = Math.sqrt(mx * mx + my * my), ry = rx * (entity.axisRatio || 1);
        paths.push(`M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy}`);
      }
      break;

    case 'SPLINE':
      if (entity.controlPoints?.length >= 2) {
        const cp = entity.controlPoints;
        let d = `M ${tx(cp[0].x)} ${ty(cp[0].y)}`;
        for (let i = 1; i < cp.length; i++) d += ` L ${tx(cp[i].x)} ${ty(cp[i].y)}`;
        paths.push(d);
      }
      break;

    case 'SOLID':
    case '3DFACE': {
      const pts = entity.points || entity.vertices;
      if (pts?.length >= 3) {
        let d = `M ${tx(pts[0].x)} ${ty(pts[0].y)}`;
        for (let i = 1; i < pts.length; i++) {
          if (pts[i]) d += ` L ${tx(pts[i].x)} ${ty(pts[i].y)}`;
        }
        d += ' Z';
        paths.push(d);
      }
      break;
    }

    case 'INSERT':
      if (blocks && entity.name && blocks[entity.name]) {
        for (const be of (blocks[entity.name].entities || [])) {
          paths.push(...entityToSvgPaths(transformInsertEntity(be, entity), offsetX, offsetY, scaleY, blocks));
        }
      }
      break;
  }
  return paths;
}

/* ──────────────────────────── MaxRects Nesting ──────────────────────────── */

interface FreeRect { x: number; y: number; width: number; height: number }
interface NestItem { partId: string; copyIndex: number; width: number; height: number; area: number }

function intersects(a: FreeRect, b: FreeRect): boolean {
  return !(a.x >= b.x + b.width || a.x + a.width <= b.x ||
           a.y >= b.y + b.height || a.y + a.height <= b.y);
}

function pruneContained(rects: FreeRect[]): FreeRect[] {
  const keep: FreeRect[] = [];
  for (let i = 0; i < rects.length; i++) {
    let inside = false;
    for (let j = 0; j < rects.length; j++) {
      if (i !== j &&
          rects[i].x >= rects[j].x && rects[i].y >= rects[j].y &&
          rects[i].x + rects[i].width <= rects[j].x + rects[j].width &&
          rects[i].y + rects[i].height <= rects[j].y + rects[j].height) {
        inside = true; break;
      }
    }
    if (!inside) keep.push(rects[i]);
  }
  return keep;
}

function splitFreeRects(freeRects: FreeRect[], placed: FreeRect): FreeRect[] {
  const next: FreeRect[] = [];
  for (const r of freeRects) {
    if (!intersects(r, placed)) { next.push(r); continue; }
    if (placed.x + placed.width < r.x + r.width)
      next.push({ x: placed.x + placed.width, y: r.y, width: r.x + r.width - (placed.x + placed.width), height: r.height });
    if (placed.x > r.x)
      next.push({ x: r.x, y: r.y, width: placed.x - r.x, height: r.height });
    if (placed.y > r.y)
      next.push({ x: r.x, y: r.y, width: r.width, height: placed.y - r.y });
    if (placed.y + placed.height < r.y + r.height)
      next.push({ x: r.x, y: placed.y + placed.height, width: r.width, height: r.y + r.height - (placed.y + placed.height) });
  }
  return pruneContained(next);
}

function maxRectsNest(
  items: NestItem[], sheetW: number, sheetH: number, gap: number, rotate: boolean,
): NestSheet[] {
  const sheets: NestSheet[] = [];
  const remaining = [...items].sort((a, b) => b.area - a.area);

  while (remaining.length > 0) {
    const sheet: NestSheet = { placements: [], utilization: 0 };
    let freeRects: FreeRect[] = [{ x: 0, y: 0, width: sheetW, height: sheetH }];
    let usedArea = 0;
    const unplaced: NestItem[] = [];

    for (const item of remaining) {
      const w = item.width + gap, h = item.height + gap;
      let best: FreeRect | null = null, bestScore = Infinity, bestRot = false;

      for (const rect of freeRects) {
        if (w <= rect.width + 0.001 && h <= rect.height + 0.001) {
          const s = rect.y + h + (rect.x + w) * 0.0001;
          if (s < bestScore) { bestScore = s; best = rect; bestRot = false; }
        }
        if (rotate && h <= rect.width + 0.001 && w <= rect.height + 0.001) {
          const s = rect.y + w + (rect.x + h) * 0.0001;
          if (s < bestScore) { bestScore = s; best = rect; bestRot = true; }
        }
      }

      if (best) {
        const pw = bestRot ? h : w, ph = bestRot ? w : h;
        sheet.placements.push({
          partId: item.partId, copyIndex: item.copyIndex,
          x: best.x, y: best.y,
          width: bestRot ? item.height : item.width,
          height: bestRot ? item.width : item.height,
          rotated: bestRot,
        });
        usedArea += item.width * item.height;
        freeRects = splitFreeRects(freeRects, { x: best.x, y: best.y, width: pw, height: ph });
      } else {
        unplaced.push(item);
      }
    }

    sheet.utilization = sheetW * sheetH > 0 ? (usedArea / (sheetW * sheetH)) * 100 : 0;
    sheets.push(sheet);
    remaining.length = 0;
    remaining.push(...unplaced);
    if (sheet.placements.length === 0) break;
  }
  return sheets;
}

/* ──────────────────────────── Public Nest API ──────────────────────────── */

export function nestParts(
  parts: Array<{ part: DxfPart; quantity: number }>,
  sheetWidth: number, sheetHeight: number, spacing: number, allowRotation: boolean,
): NestingResult {
  const items: NestItem[] = [];
  for (const { part, quantity } of parts) {
    for (let i = 0; i < quantity; i++) {
      items.push({ partId: part.id, copyIndex: i, width: part.bbox.width, height: part.bbox.height, area: part.bbox.width * part.bbox.height });
    }
  }
  const sheets = maxRectsNest(items, sheetWidth, sheetHeight, spacing, allowRotation);
  const placedParts = sheets.reduce((s, sh) => s + sh.placements.length, 0);
  const totalPartArea = sheets.reduce((s, sh) => sh.placements.reduce((a, p) => a + p.width * p.height, 0) + s, 0);
  const totalSheetArea = sheets.length * sheetWidth * sheetHeight;
  return {
    sheets, totalParts: items.length, placedParts, totalSheets: sheets.length,
    overallUtilization: totalSheetArea > 0 ? (totalPartArea / totalSheetArea) * 100 : 0,
  };
}
